// Shapes returned by omg-api GET /admin/health/* (OMG-2767). Dates arrive as
// ISO strings over JSON.

export type MonitorConclusion =
  | "success"
  | "failure"
  | "cancelled"
  | "skipped"
  | "timed_out";

export type MonitorState = "failing" | "stale" | "ok" | "unknown";

export interface MonitorStatus {
  source: string;
  monitor: string;
  state: MonitorState;
  latest: {
    conclusion: MonitorConclusion;
    finishedAt: string;
    runUrl: string | null;
    summary: string | null;
    event: string | null;
  };
  /** Newest first. */
  history: MonitorConclusion[];
  lastSuccessAt: string | null;
  /** Longest usual gap between runs (90th percentile), hours. */
  expectedGapHours: number | null;
  ageHours: number;
}

export interface MonitorsResponse {
  branch: string;
  generatedAt: string;
  monitors: MonitorStatus[];
}

export interface CurrencyAmount {
  currency: string;
  amount: string;
  /** Sales the amount covers (per week, averaged, for the baseline). Absent
   *  from an API without OMG-2793, like `usd`. */
  count?: number;
  /** The amount in US dollars with the mispricing check (OMG-2793); null
   *  when the currency has no rate. */
  usd?: CurrencyUsd | null;
}

export interface CurrencyUsd {
  unitsPerUsd: number;
  rateDate: string;
  revenue: number;
  averageOrder: number;
  expectedAverageOrder: number | null;
  ratio: number | null;
  flag: "low" | "high" | null;
}

export interface PurchasesResponse {
  timezone: string;
  generatedAt: string;
  today: { count: number; revenue: CurrencyAmount[] };
  baseline: {
    weeks: { weeksAgo: number; count: number }[];
    averageCount: number;
    averageRevenue: CurrencyAmount[];
  };
  changePercent: number | null;
  daily: { day: string; count: number }[];
  needsAttention: {
    abandoned: number;
    stuck: number;
    voidFailed: number;
    settlementFailed: number;
    hasMore: boolean;
  };
}

// GET /admin/health/perf: the nightly perf probe, one series per site.

export interface PerfFinding {
  severity: "fail" | "warn" | "error";
  code: string;
  message: string;
  /** Excused by a dated known-failures row: reported, does not page. */
  known: boolean;
}

export interface PerfPoint {
  at: string;
  nonMediaBytes: number | null;
  mediaBytes: number | null;
  scriptBytes: number | null;
  imageBytes: number | null;
  requests: number | null;
  lcpMs: number | null;
  fcpMs: number | null;
  ttfbMs: number | null;
  tbtMs: number | null;
  cls: number | null;
}

// The Lighthouse view (OMG-2795): five lab metrics per site, each with
// Google's band, plus the diagnostics behind them.

export type PerfMetricKey = "lcp" | "cls" | "tbt" | "fcp" | "ttfb";
export const PERF_METRICS: PerfMetricKey[] = [
  "lcp",
  "cls",
  "tbt",
  "fcp",
  "ttfb",
];

export type PerfBand = "good" | "needs-improvement" | "poor";

export interface PerfThreshold {
  good: number;
  poor: number;
  unit: "ms" | "score";
  label: string;
}

export interface PerfMetric {
  value: number | null;
  band: PerfBand | null;
}

export interface PerfResourceRow {
  /** host + path, cut by the probe; not a full URL. */
  url: string;
  kind: string;
  bytes: number | null;
}

export interface PerfDetail {
  heaviest: PerfResourceRow[];
  renderBlocking: PerfResourceRow[];
  renderBlockingCount: number | null;
  thirdParty: { host: string; bytes: number | null; requests: number | null }[];
  thirdPartyCount: number | null;
  images: {
    count: number | null;
    bytes: number | null;
    legacyFormat: number | null;
    oversized: number | null;
    eagerOffscreen: number | null;
  } | null;
}

export interface PerfLcpElement {
  tag: string | null;
  src: string | null;
  loading: string | null;
}

export interface PerfHostTrend {
  host: string;
  /** Readable name from the probe ("www /checkout"). */
  label: string;
  url: string | null;
  /** Oldest to newest. */
  points: PerfPoint[];
  /** The newest night's metrics with their band. */
  metrics: Record<PerfMetricKey, PerfMetric>;
  lcpElement: PerfLcpElement | null;
  findings: PerfFinding[];
  /** Null for nights before the probe reported detail. */
  detail: PerfDetail | null;
}

export interface PerfResponse {
  generatedAt: string;
  latest: {
    capturedAt: string | null;
    conclusion: MonitorConclusion;
    runUrl: string | null;
    paging: boolean | null;
  } | null;
  hosts: PerfHostTrend[];
  thresholds: Record<PerfMetricKey, PerfThreshold>;
  skippedRuns: number;
}

// GET /admin/health/priorities (OMG-2798): the "most worth fixing" list.

export type Effort = "cheap" | "medium" | "expensive";
export type Impact = "high" | "medium" | "low";
export type PriorityKind = "perf" | "monitor" | "purchases";

export interface PageWeight {
  label: string;
  traffic: number;
  revenue: number;
}

export type PriorityDetail =
  | {
      kind: "perf";
      host: string;
      url: string | null;
      weights: PageWeight;
      lcpElement: PerfLcpElement | null;
      findings: PerfFinding[];
      probe: PerfDetail | null;
    }
  | { kind: "monitor"; monitor: MonitorStatus; failRate: number }
  | {
      kind: "purchases";
      todayCount: number;
      averageCount: number;
      changePercent: number | null;
      revenueAtStake: CurrencyAmount | null;
      needsAttention: PurchasesResponse["needsAttention"] | null;
    };

export interface PriorityItem {
  id: string;
  kind: PriorityKind;
  title: string;
  subject: string;
  metric: PerfMetricKey | null;
  value: number | null;
  band: PerfBand | null;
  score: number;
  impact: Impact;
  impactWhy: string;
  effort: Effort;
  effortWhy: string;
  /** The live page, the run, or an in-app path such as /transactions/needs-attention. */
  link: string | null;
  detail: PriorityDetail;
}

export interface PrioritiesResponse {
  generatedAt: string;
  /** Highest score first. */
  items: PriorityItem[];
  weights: {
    source: "static";
    note: string;
    pages: Record<string, PageWeight>;
  };
  purchasesIncluded: boolean;
  leftOut: string[];
}
